/* global APP_VERSION */
import React, { Fragment } from 'react'
import PropTypes from 'prop-types'
import { injectIntl, FormattedMessage } from 'react-intl'
import reduce from 'lodash/reduce'
import Modal from '@vtex/styleguide/lib/Modal'
import * as CustomEvent from '@/checkout-instore-legacy/utils/CustomEvent'

import { intlShape } from '@/checkout-instore-legacy/i18n/intlShape'
import Icon from '@/checkout-instore-legacy/components/Commons/Icons'
import * as SidebarUtils from '@/checkout-instore-legacy/utils/SidebarUtils'
import { verifyRedirectToInStoreCore } from '@/checkout-instore-legacy/utils/verifyRedirectToInStoreCore'
import { OMS_ORDER_PATH } from '@/checkout-instore-legacy/constants/OMSConstants'
import * as URLUtils from '@/checkout-instore-legacy/utils/UrlUtils'
import { sendEvent } from '@/checkout-instore-legacy/utils/WebViewBridge'
import showInfoInConsole from '@/checkout-instore-legacy/utils/showInfoInConsole'
import {
  goToHome,
  goToCustomLink,
} from '@/checkout-instore-legacy/utils/routesUtils'
import { logClickEvent } from '@/checkout-instore-legacy/utils/EventHelper'
import { getEcommerceIframeUrl } from '@/checkout-instore-legacy/share/utils'
import ErrorMessage from '@/checkout-instore-legacy/components/Commons/ErrorMessage'
import { ICON_SIZE } from '@/checkout-instore-legacy/constants/SizeConstants'
import Pattern from '@/checkout-instore-legacy/assets/images/vtex-pattern-header.svg'
import { getInstoreConfig } from '@/checkout-instore-legacy/utils/configUtils'
import Swipable from '@/checkout-instore-legacy/components/Commons/Swipable'
import { clearAllCache } from '@/checkout-instore-legacy/cache/utils'

import SidebarVendorName from './SidebarVendorName'
import SidebarItem from './SidebarItem'
import { defaultMemoize } from 'reselect'
import { CSSTransitionGroup } from '../CSSTransitionGroup'

const baseErrorMsg = 'Sales App Custom Link:'

class Sidebar extends React.Component {
  constructor(props) {
    super(props)

    this.state = {
      accountName: URLUtils.getAccountName().toUpperCase(),
      customLinks: [],
      isModalOpen: false,
      isModalSubmitted: false,
    }

    this.menuContainer = null
    this.menuOverlay = null
    this.menuOffset = 0
    this.dragging = false
    this.dragEnabled = false
    this.dragLock = false
    this.dragStartPos = {
      x: 0,
      y: 0,
    }
    this.lastDragPos = []
  }

  handleDocumentTouchDown = (event) => {
    if (event.target.id === 'menu-overlay') {
      event.preventDefault()
    }
  }

  componentDidMount() {
    // We put in this component since we dont want to render it on inStore Core
    verifyRedirectToInStoreCore()
    CustomEvent.listenEvent('load.instore-custom', () => {
      verifyRedirectToInStoreCore()
    })

    window.addEventListener(
      'addCustomLink.instore',
      this.handleAddCustomLink,
      false
    )

    sendEvent({
      type: 'requestAppInfo',
    })

    document.addEventListener('touchstart', this.handleDocumentTouchDown, {
      passive: false,
    })
  }

  componentWillUnmount() {
    window.removeEventListener(
      'addCustomLink.instore',
      this.handleAddCustomLink
    )
    document.removeEventListener('touchstart', this.handleDocumentTouchDown)

    CustomEvent.unlistenEvent(
      'load.instore-custom',
      verifyRedirectToInStoreCore
    )
  }

  addCustomLink = (newLink) => {
    const links = this.state.customLinks
    links.push(newLink)

    // Filter only valid links and transform them to a valid format
    const validLinks = reduce(
      links,
      (memo, link) => {
        // Validate required fields
        if (!link.title) {
          console.error(
            `${baseErrorMsg} Required field \`title\` is not defined.`,
            link
          )
          return memo
        }
        if (!link.href && !link.handleClick) {
          console.error(
            `${baseErrorMsg} Required fields \`href\` and \`handleClick\` are not defined. You must define at least one of them`,
            link
          )
          return memo
        }

        // Add to our links array
        return memo.concat({
          section: link.section,
          title: link.title,
          handleClick: () => {
            this.handleCustomLinkClick(link)
          },
        })
      },
      []
    )

    this.setState({ customLinks: validLinks })
  }

  handleLogoutClick = () => {
    const { router, setSidebarVisibility } = this.props
    setSidebarVisibility(false)
    this.props.logout(router)
  }

  handleCustomLinkClick = (link) => {
    const { nativeAppInfo, router } = this.props
    goToCustomLink(link, nativeAppInfo, router)

    this.handleCloseSidebar()
  }

  handleAddCustomLink = (e) => {
    const detail = e.detail || (e.data && e.data.detail)
    if (!detail) {
      console.error(
        `${baseErrorMsg} The required field \`detail\` in the event is not defined.`,
        e
      )
      return
    }

    // Validate type of property supplied
    if (typeof detail !== 'object') {
      console.error(
        `${baseErrorMsg} Invalid type for event property \`detail\`. A \`${typeof detail}\` was supplied, expected an \`array\` or \`object\`.`
      )
      return
    }

    let links = detail

    if (!Array.isArray(links)) {
      links = [links]
    }

    for (const i in links) {
      this.addCustomLink(links[i])
    }
  }

  getDefaultSidebarLinks() {
    const { router, intl } = this.props

    const vendor = this.props.vendor || {}
    const isLoggedIn = vendor.name !== undefined && vendor.name !== null
    const isAdmin =
      isLoggedIn && vendor.name && vendor.name.toLowerCase() === 'developer'
    const instoreConfig = getInstoreConfig()

    const fullScreenOption = window.isFullScreen
      ? [
          {
            title: window.isFullScreen() ? (
              <FormattedMessage id="leaveFullScreen" />
            ) : (
              <FormattedMessage id="enterFullScreen" />
            ),
            handleClick: SidebarUtils.toogleFullScreen,
            icon: 'nc-zoom-e',
          },
        ]
      : []

    const configureDeviceOption = instoreConfig.configureDeviceEnabled
      ? [
          {
            title: <FormattedMessage id="configureDevice" />,
            id: 'sidebar-device-config',
            icon: 'nc-device-settings',
            handleClick: () => {
              SidebarUtils.configureDevice(router)
              this.handleCloseSidebar()
            },
          },
        ]
      : []

    const storePerformanceOption = instoreConfig.enableStorePerformance
      ? [
          {
            title: <FormattedMessage id="storePerformance" />,
            id: 'store-performance',
            icon: 'nc-store-performance',
            handleClick: () => {
              logClickEvent('dashboard-menu', 'Admin')
              SidebarUtils.dashboard(router)
              this.handleCloseSidebar()
            },
          },
        ]
      : []

    return [
      {
        section: 'priority',
        isHidden: !isLoggedIn,
        options: [
          {
            title: <FormattedMessage id="newSale" />,
            id: 'sidebar-new-sale',
            icon: 'nc-cart-simple-add',
            handleClick: () => {
              logClickEvent('new-order-menu', 'Admin')
              SidebarUtils.newOrder()
              this.handleCloseSidebar()
            },
          },
        ],
      },
      getInstoreConfig().transferEnabled && {
        section: 'priority',
        isHidden: !isLoggedIn,
        options: [
          {
            title: <FormattedMessage id="cartCapture" />,
            id: 'sidebar-cart-capture',
            icon: 'nc-cart-capture',
            handleClick: () => {
              logClickEvent('cart-capture-menu', 'Admin')
              SidebarUtils.cartCapture(router)
              this.handleCloseSidebar()
            },
          },
        ],
      },
      {
        section: 'ops',
        title: <FormattedMessage id="sidebarSales" />,
        isHidden: !isLoggedIn,
        options: [
          {
            title: <FormattedMessage id="ongoingSales" />,
            id: 'orders',
            icon: 'nc-cart-speed-1',
            handleClick: () => {
              SidebarUtils.goToOrderForms(router)
              this.handleCloseSidebar()
            },
          },
          {
            title: <FormattedMessage id="lastOrders" />,
            id: 'last-orders',
            icon: 'nc-bag-22',
            handleClick: () => {
              SidebarUtils.goToLastOrders(router)
              this.handleCloseSidebar()
            },
          },
          getInstoreConfig().ecommerceMenuItem && {
            title: <FormattedMessage id="sidebar.ecommerceMenuItem" />,
            id: 'ecommerce-menu-item',
            icon: 'nc-cart-speed-1',
            handleClick: () => {
              this.handleCustomLinkClick({
                title: intl.formatMessage({
                  id: 'sidebar.ecommerceMenuItem',
                }),
                openExternal: true,
                href: getEcommerceIframeUrl(),
                isEcommerce: true,
              })
            },
          },
          getInstoreConfig().returnExchangeEnabled && {
            title: <FormattedMessage id="sidebar.returnExchange" />,
            id: 'return-exchange',
            icon: 'nc-return-exchange',
            handleClick: () => {
              SidebarUtils.goToReturnExchange(router)
              this.handleCloseSidebar()
            },
          },
          ...storePerformanceOption,
        ],
      },
      {
        section: 'advanced',
        title: <FormattedMessage id="sidebarConfiguration" />,
        options: [
          {
            title: <FormattedMessage id="clearAllCache" />,
            id: 'sidebar-clear-all-cache',
            icon: 'nc-undo-29',
            handleClick: () => {
              clearAllCache().finally(() => {
                this.handleCloseSidebar()
                window.location.reload()
              })
            },
          },
          {
            title: <FormattedMessage id="reportError" />,
            id: 'sidebar-report-error',
            icon: 'nc-alert',
            handleClick: this.handleSidebarBottomInfoClick,
          },
          ...fullScreenOption,
          ...configureDeviceOption,
        ],
      },
      {
        section: 'dev',
        title: <FormattedMessage id="sidebarDevelopment" />,
        isHidden: !isAdmin,
        options: [
          {
            title: <FormattedMessage id="oms" defaultMessage="VTEX Orders" />,
            handleClick: () => {
              this.handleCustomLinkClick({
                title: intl.formatMessage({
                  id: 'oms',
                  defaultMessage: 'VTEX Orders',
                }),
                openExternal: true,
                href: OMS_ORDER_PATH,
              })
            },
          },
          {
            title: <FormattedMessage id="changeVendor" />,
            id: 'logout',
            handleClick: () => {
              this.props.setSidebarVisibility(false)
              this.props.logout(router)
            },
          },
          {
            title: <FormattedMessage id="sidebarHome" />,
            id: 'home',
            handleClick: () => {
              goToHome(router)
              this.handleCloseSidebar()
            },
          },
          {
            title: <FormattedMessage id="changePaymentHardware" />,
            handleClick: () => {
              SidebarUtils.changePaymentHardware()
            },
          },
          {
            title: 'Limpar cache',
            handleClick: () => {
              SidebarUtils.clearNavigationData()
            },
          },
          {
            title: 'Usar ambiente alpha',
            id: 'choose-alpha',
            handleClick: () => {
              SidebarUtils.setAlphaEnvironment()
            },
          },
          {
            title: 'Usar ambiente beta',
            handleClick: () => {
              SidebarUtils.setEnvironment('beta')
            },
          },
          {
            title: 'Usar ambiente stable',
            handleClick: () => {
              SidebarUtils.setEnvironment('stable')
            },
          },
          {
            title: 'Usar ambiente de desenvolvimento',
            handleClick: () => {
              SidebarUtils.setEnvironment('dev')
            },
          },
          {
            title: 'Redefinir configurações',
            handleClick: () => {
              SidebarUtils.resetSettings()
            },
          },
          {
            title: 'Ligar cache do catálogo',
            handleClick: () => {
              this.props.enableCache()
              this.handleCloseSidebar()
            },
          },
          {
            title: 'Desligar cache do catálogo',
            handleClick: () => {
              this.props.disableCache()
              this.handleCloseSidebar()
            },
          },
        ],
      },
    ]
  }

  handleOpenModal = () => {
    this.setState({ isModalOpen: true })
  }

  handleCloseModal = () => {
    this.setState({ isModalOpen: false, isModalSubmitted: false })
  }

  handleSubmitModal = () => {
    this.setState({ isModalSubmitted: true })
  }

  getSidebarLinkHelperMemo = defaultMemoize(this.getSidebarLinksHelper)

  getSidebarLinksHelper(customLinks, defaultOptions) {
    const sidebarLinks = [...defaultOptions]

    if (customLinks && customLinks.length) {
      customLinks.forEach((link) => {
        let sectionExists = false

        sidebarLinks.forEach((group) => {
          if (link && group && group.section === link.section) {
            sectionExists = true
            group.options.push(link)
          }
        })

        if (!sectionExists) {
          sidebarLinks.push({
            section: link.section,
            title: link.section,
            options: [link],
          })
        }
      })
    }

    return sidebarLinks
  }

  getSidebarLinks() {
    const { customLinks } = this.state
    const defaultOptions = this.getDefaultSidebarLinks()

    return this.getSidebarLinkHelperMemo(customLinks, defaultOptions)
  }

  handleCloseSidebar = () => {
    this.props.setSidebarVisibility(false)
  }

  handleSidebarBottomInfoClick = () => {
    this.handleCloseSidebar()
    this.setState({ isModalOpen: true })
  }

  handleLockScroll = () => {
    this.menuContainer.classList.add('overflow-hidden')
    this.menuContainer.classList.remove('overflow-auto')
  }
  handleUnlockScroll = () => {
    this.menuContainer.classList.add('overflow-auto')
    this.menuContainer.classList.remove('overflow-hidden')
  }

  render() {
    const isOpen = this.props.isOpen
    const vendor = this.props.vendor || {}

    const sidebarLinks = this.getSidebarLinks()
    const { accountName } = this.state

    let nativeAppInfo = ''
    if (this.props.nativeAppInfo !== null) {
      nativeAppInfo = (
        <span>
          <div id="native-os-platform" className="dib">
            {this.props.nativeAppInfo.platform.toUpperCase()}
          </div>
          {' - v'}
          <div id="native-app-version" className="dib">
            {this.props.nativeAppInfo.version}
          </div>
        </span>
      )
    }

    showInfoInConsole({
      platform: this.props.nativeAppInfo && this.props.nativeAppInfo.platform,
      nativeVersion:
        this.props.nativeAppInfo && this.props.nativeAppInfo.version,
      accountName,
      spaVersion: APP_VERSION,
    })

    return (
      <Fragment>
        <div
          className={`aspect-ratio--object ${
            isOpen ? 'pointer-events-auto' : 'pointer-events-none'
          }`}
        >
          <CSSTransitionGroup
            transitionName="fadeSlow"
            transitionEnterTimeout={200}
            transitionLeaveTimeout={200}
          >
            {isOpen && (
              <div>
                <div
                  id="menu-overlay"
                  ref={(el) => {
                    this.menuOverlay = el
                  }}
                  key="menu-overlay"
                  className={`aspect-ratio--object bg-black-40 ${
                    isOpen ? 'pointer-events-all' : 'pointer-events-none'
                  }`}
                  onTouchStart={this.handleCloseSidebar}
                  onMouseDown={this.handleCloseSidebar}
                />
              </div>
            )}
          </CSSTransitionGroup>

          <CSSTransitionGroup
            transitionName="slideRight"
            transitionEnterTimeout={200}
            transitionLeaveTimeout={200}
          >
            {isOpen && (
              <div
                className="absolute z-999 vh-100 menu-container"
                key="menu-container"
              >
                <Swipable
                  onSwipeLeft={this.handleCloseSidebar}
                  onLockScroll={this.handleLockScroll}
                  onUnlockScroll={this.handleUnlockScroll}
                >
                  <div
                    className="absolute left-0 vh-100 w-100 bg-base overflow-auto momentum hide-scrollbar"
                    ref={(el) => {
                      this.menuContainer = el
                    }}
                  >
                    <div
                      className="w-100 ph5 pb5 pt8 lh-title w-100 bg-action-primary c-on-action-primary"
                      style={{
                        backgroundImage: `url(${Pattern})`,
                        backgroundPosition: '50% 50%',
                        backgroundSize: 'cover',
                      }}
                    >
                      <div className="fw7 pt6 lh-solid">
                        <FormattedMessage id="vendorGreeting" />
                      </div>
                      <div className="flex justify-between items-end lh-solid">
                        <SidebarVendorName name={vendor.name} />
                        <button
                          id="sidebar-logout"
                          className="flex pointer pb3 white bg-transparent bn"
                          onClick={this.handleLogoutClick}
                        >
                          <div className="pt3 f5 flex fw7">
                            <FormattedMessage id="vendorLogout" />
                          </div>
                          <div className="pl4 self-center">
                            <Icon
                              type="nc-logout"
                              color="#FFFFFF"
                              size={ICON_SIZE}
                            />
                          </div>
                        </button>
                      </div>
                    </div>
                    <div className="flex-auto flex-column pt5">
                      {sidebarLinks.map((group, i) => (
                        <div
                          key={i}
                          className={`${group.isHidden ? 'dn' : ''}`}
                        >
                          {group.title && (
                            <div>
                              <hr className="w2 pl5 mt2 mb3 pb2 pt3 ml0 b--black-10 bt-0 bl-0 br-0 bb bw1" />
                              <div className="f6 pl5 ml4-ns pl4-l pl4-ns ttu black-50 mt5 pt3 mb2 pb3">
                                <span>{group.title}</span>
                              </div>
                            </div>
                          )}
                          {group.options &&
                            group.options.map((action, j) => (
                              <SidebarItem key={j} {...action} />
                            ))}
                        </div>
                      ))}
                    </div>
                    <div className="tl f7 lh-copy mid-gray mv3 pl5 mv4">
                      <div id="sidebar-account-name" className="dib">
                        {accountName}
                      </div>
                      <div className="flex">
                        {nativeAppInfo}
                        {' | '}
                        <div id="app-version" className="db">
                          {APP_VERSION}
                        </div>
                      </div>
                    </div>
                  </div>
                </Swipable>
              </div>
            )}
          </CSSTransitionGroup>
        </div>
        <Modal
          centered
          isOpen={this.state.isModalOpen}
          onClose={this.handleCloseModal}
        >
          <ErrorMessage
            onSubmitModal={this.handleSubmitModal}
            onCloseModal={this.handleCloseModal}
          />
        </Modal>
      </Fragment>
    )
  }
}

Sidebar.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setSidebarVisibility: PropTypes.func.isRequired,
  router: PropTypes.any,
  nativeAppInfo: PropTypes.object,
  intl: intlShape.isRequired,
  logout: PropTypes.func.isRequired,
  vendor: PropTypes.object,
  enableCache: PropTypes.func.isRequired,
  disableCache: PropTypes.func.isRequired,
}

export default injectIntl(Sidebar)
